import {
	API_DATA_PERMISSIONS,
	type ApiDataPermission,
} from "@repo/shared-primitives"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { PageHeader } from "@/components/layout"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PriceDataHoldersTable } from "@/features/api-data-access/PriceDataHoldersTable"
import { PERMISSION_LABELS } from "@/features/api-data-access/price-data-labels"
import { RefusedCallsTable } from "@/features/api-data-access/RefusedCallsTable"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useDataPermissionRefusals } from "@/queries/marketplace"

const TABS = [...API_DATA_PERMISSIONS, "refused"] as const
type PriceDataTab = (typeof TABS)[number]

export const Route = createFileRoute("/price-data-access/")({
	component: PriceDataAccessPage,
	validateSearch: createTabSearch(TABS),
})

function isPermission(tab: PriceDataTab): tab is ApiDataPermission {
	return tab !== "refused"
}

/** Who may read price data through the API, per permission, and who was refused. */
function PriceDataAccessPage() {
	const { tab = "market-values" } = useSearch({ from: "/price-data-access/" })
	const navigate = useNavigate()
	const refusals = useDataPermissionRefusals()
	const refusedCount = refusals.data?.rows.length

	return (
		<div className="flex flex-col gap-4 p-6">
			<PageHeader
				title="Price data access"
				description="Who may read price data from other marketplaces through the API, and what they used in the last 30 days"
			/>

			<Tabs
				value={tab}
				onValueChange={(next) =>
					navigate({
						to: "/price-data-access",
						search:
							next === "market-values" ? {} : { tab: next as PriceDataTab },
						replace: true,
					})
				}
			>
				<TabsList variant="line">
					{API_DATA_PERMISSIONS.map((permission) => (
						<TabsTrigger key={permission} value={permission} className="px-3">
							{PERMISSION_LABELS[permission]}
						</TabsTrigger>
					))}
					<TabsTrigger value="refused" className="px-3">
						Refused calls
						{refusedCount ? (
							<span className="font-mono text-[11px] text-warn tabular-nums">
								{refusedCount}
							</span>
						) : null}
					</TabsTrigger>
				</TabsList>
			</Tabs>

			{isPermission(tab) ? (
				<PriceDataHoldersTable permission={tab} />
			) : (
				<RefusedCallsTable />
			)}
		</div>
	)
}
